import { z } from 'zod';
import { StorageObjectKeySchema } from '../storage.js';
import {
  CommercialProposalModeSchema,
  CommercialProposalPaymentSchemeSchema,
  CommercialProposalTariffUnitSchema,
} from './status.js';

// Field caps. The prototype is a free-text internal tool; these caps keep
// the persisted JSON deterministically small and protect the public view
// from oversized payloads. Sized generously against the prototype defaults.
export const CP_CLIENT_MAX = 500;
export const CP_PRODUCT_NAME_MAX = 300;
export const CP_CHARACTERISTIC_MAX = 300;
export const CP_CHARACTERISTICS_MAX_LINES = 100;
export const CP_TERM_MAX = 200;
export const CP_DESTINATION_MAX = 300;
export const CP_PLACES_MAX = 200;
export const CP_NOTES_MAX = 4000;
export const CP_MANAGER_NAME_MAX = 200;
export const CP_MANAGER_PHONE_MAX = 64;

// WHY: monetary/quantity inputs are non-negative finite numbers. Rates are
// strictly positive (a zero rate would divide-by-zero or zero out the
// rouble total). The default divisors/insurance rate mirror the prototype
// advanced-mode defaults exactly.
const NonNegativeNumber = z.number().finite().nonnegative();
const PositiveNumber = z.number().finite().positive();
export const COMMERCIAL_PROPOSAL_TOTAL_CURRENCIES = ['RUB', 'USD', 'CNY'] as const;
export const CommercialProposalTotalCurrencySchema = z.enum(COMMERCIAL_PROPOSAL_TOTAL_CURRENCIES);
export type CommercialProposalTotalCurrency = z.infer<typeof CommercialProposalTotalCurrencySchema>;

/**
 * @aiPurpose ordered plain-text characteristics lines for the product
 *   (#1194). Mirrors the prototype textarea where each line is one
 *   characteristic; order is meaningful and preserved. Plain text ONLY —
 *   no rich HTML — to keep the public projection inert.
 * @aiInvariant lines are trimmed-non-empty individually; the array order
 *   is the display order.
 */
const CharacteristicLineSchema = z
  .string()
  .min(1)
  .max(CP_CHARACTERISTIC_MAX)
  .refine((s) => s.trim().length > 0, 'characteristic line must not be whitespace-only');

/**
 * @aiPurpose canonical input content of a commercial proposal (#1194).
 *   Covers every MVP field surfaced by the prototype form: header/rates,
 *   product, buyout, logistics, advanced coefficients, notes, and the
 *   manager signature. This is the EDITABLE draft input — calculated
 *   outputs live in `CommercialProposalCalculationOutputSchema`, and a
 *   published proposal freezes this object verbatim into its snapshot.
 * @aiInvariant `.strict()` so a typo (e.g. `goodsCny` vs `goodCny`)
 *   surfaces as a 400 instead of silently dropping a costed input. No
 *   rich text / arbitrary HTML is accepted anywhere; `characteristics`
 *   and the notes fields are plain text only.
 * @aiPii free-text fields (`client`, `notes`, manager signature) may carry
 *   commercial / contact context. The public projection re-exposes them
 *   deliberately for the customer-facing sheet; internal audit feeds must
 *   not snapshot them by id-only convention.
 */
export const CommercialProposalContentSchema = z
  .object({
    // Header / rates.
    /// Internal КП composition (#1410): BOTH (выкуп + логистика, default),
    /// BUYOUT-only, or LOGISTICS-only. Defaulting to BOTH keeps every
    /// already-published/stored content object valid and its output unchanged.
    mode: CommercialProposalModeSchema.default('BOTH'),
    /// ISO date (YYYY-MM-DD) the proposal is issued on; the prototype's
    /// "Дата" field. Date-only on the wire — time-of-day is irrelevant.
    proposalDate: z.string().date(),
    /// Free-text addressee ("Кому / клиент"). May be empty in a draft.
    client: z.string().max(CP_CLIENT_MAX),
    /// Manual CNY->RUB rate (roubles per 1 yuan). Strictly positive.
    cnyRateRub: PositiveNumber,
    /// Manual USD->RUB rate (roubles per 1 dollar). Strictly positive.
    usdRateRub: PositiveNumber,
    // WHY (#2747): stage 1 (#2749) made these optional compatibility-reader
    // fields. Stage 2 defaults them so legacy content keeps its RUB display
    // while every subsequent write persists an explicit customer choice.
    buyoutTotalCurrency: CommercialProposalTotalCurrencySchema.default('RUB'),
    logisticsTotalCurrency: CommercialProposalTotalCurrencySchema.default('RUB'),
    grandTotalCurrency: CommercialProposalTotalCurrencySchema.default('RUB'),

    // Product.
    productName: z.string().min(1).max(CP_PRODUCT_NAME_MAX),
    /// Ordered plain-text characteristics lines (display order preserved).
    characteristics: z.array(CharacteristicLineSchema).max(CP_CHARACTERISTICS_MAX_LINES),
    /// Optional object-storage key for the product photo. Reuses the
    /// shared storage-key contract; the actual upload scope ships in
    /// #1196. NEVER surfaced raw to the public — see the public DTO.
    photoObjectKey: StorageObjectKeySchema.optional(),

    // Buyout.
    buyoutTerm: z.string().max(CP_TERM_MAX),
    goodsCny: NonNegativeNumber,
    chinaDeliveryCny: NonNegativeNumber,
    paymentScheme: CommercialProposalPaymentSchemeSchema,

    // Logistics.
    destination: z.string().max(CP_DESTINATION_MAX),
    logisticsTerm: z.string().max(CP_TERM_MAX),
    /// Free-text places count ("Кол-во мест"). Dropped from the editor and
    /// the customer sheet (#1244) but kept OPTIONAL so already-published
    /// content that still carries the field re-parses through `.strict()`.
    places: z.string().max(CP_PLACES_MAX).optional(),
    weightKg: NonNegativeNumber,
    volumeM3: NonNegativeNumber,
    tariffUsdPerKg: NonNegativeNumber,
    /// Logistics tariff basis (#1411): 'kg' multiplies `tariffUsdPerKg` by
    /// `weightKg`, 'm3' by `volumeM3`. Default 'kg' keeps stored content
    /// byte-identical. WHY: the numeric field stays named `tariffUsdPerKg`
    /// (now a per-selected-unit tariff) — renaming it would force a JSONB
    /// migration we deliberately avoid.
    tariffUnit: CommercialProposalTariffUnitSchema.default('kg'),
    packagingUsd: NonNegativeNumber,
    prrUsd: NonNegativeNumber,

    // Advanced coefficients.
    servicePercent: NonNegativeNumber,
    /// No-VAT divisor (prototype default 0.9). Strictly positive — it is a
    /// divisor of the rouble total.
    noVatDivisor: PositiveNumber.default(0.9),
    /// VAT divisor (prototype default 0.8). Strictly positive.
    vatDivisor: PositiveNumber.default(0.8),
    insurancePercent: NonNegativeNumber,
    /// CNY/USD rate used for the insurance basis (prototype default 7.2).
    insuranceCnyPerUsdRate: PositiveNumber.default(7.2),

    // Notes / signature. Plain text; newlines preserved verbatim.
    buyoutNotes: z.string().max(CP_NOTES_MAX),
    logisticsNotes: z.string().max(CP_NOTES_MAX),
    managerName: z.string().max(CP_MANAGER_NAME_MAX),
    managerPhone: z.string().max(CP_MANAGER_PHONE_MAX),
  })
  .strict();
export type CommercialProposalContent = z.infer<typeof CommercialProposalContentSchema>;
